/**
 * Polymarket US number display, from the text itself.
 *
 * US prices, quantities and amounts are exact decimal text. These helpers
 * group the integer part and pad dollars to at least two decimals without
 * parsing into a float: nothing is rounded or truncated, and extra digits are
 * kept ("1783" → "$1,783.00", "-0.0025" → "-$0.0025"). Text that is not a
 * plain decimal is shown as given. Display only: these strings never feed a
 * ticket, edit or close input.
 */

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

type Parts = { negative: boolean; whole: string; fraction: string };

function parts(text: string): Parts | null {
  const m = DECIMAL.exec(text);
  if (!m) return null;
  const whole = m[2].replace(/^0+(?=\d)/, '');
  const fraction = m[3] ?? '';
  // A negative zero ("-0", "-0.000") is shown without its sign.
  const zero = /^0+$/.test(whole) && /^0*$/.test(fraction);
  return { negative: m[1] === '-' && !zero, whole, fraction };
}

const group = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Exact decimal text with its integer part grouped: "1234567.5" → "1,234,567.5". */
export function formatUsDecimal(text: string): string {
  const p = parts(text);
  if (!p) return text;
  return `${p.negative ? '-' : ''}${group(p.whole)}${p.fraction ? `.${p.fraction}` : ''}`;
}

/** Exact USD text as dollars: grouped, at least two decimals, every extra
 * digit kept, and the sign before the dollar sign ("-$0.0025"). */
export function formatUsUsd(text: string): string {
  const p = parts(text);
  if (!p) return `$${text}`;
  return `${p.negative ? '-' : ''}$${group(p.whole)}.${p.fraction.padEnd(2, '0')}`;
}

/** Exact signed USD text as a P&L amount: "+$0.73", "-$0.0125", "$0.00" for
 * zero (#1227, the K/P `showSign` shape). */
export function formatUsSignedUsd(text: string): string {
  if (isUsZero(text)) return formatUsUsd('0');
  return text.startsWith('-') ? formatUsUsd(text) : `+${formatUsUsd(text)}`;
}

/** Zero in any spelling the relay sends ("0", "-0.000"). */
export const isUsZero = (text: string) => /^-?0(?:\.0+)?$/.test(text);

/** USD price text → cents text by moving the decimal point two places
 * ("0.4567" → "45.67", "0.425" → "42.5"). Null for text that is not a plain
 * non-negative decimal. */
export function usUsdToCentsText(text: string): string | null {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) return null;
  const fraction = (m[2] ?? '').padEnd(2, '0');
  const whole = `${m[1]}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const rest = fraction.slice(2).replace(/0+$/, '');
  return rest ? `${whole}.${rest}` : whole;
}

/** A US price as the cents label every G1 venue shows ("0.425" → "42.5¢"),
 * exact to the sub-cent. Text that is not a plain price is shown as dollars,
 * as given. */
export function usPriceCentsLabel(text: string): string {
  const cents = usUsdToCentsText(text);
  return cents === null ? formatUsUsd(text) : `${cents}¢`;
}
